import { useCallback, useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { Job, jobQueueExpandedState } from '~/state/atoms/jobAtoms';
import { PollData } from '~/backend/types';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { useJobManagement } from '../useJobManagement';
import {
    downloadBackendResults,
    DownloadedResults,
    pollForResults,
    uploadFiles,
} from './utils';
import { handleError } from '~/utils/errors';
import {
    createCompositeCbctTask,
    createImageDataset,
    createRegistrationTask,
    deleteTaskResult,
    downloadTaskResults,
    monitorCreatedTask,
    uploadDatasetFiles,
} from './utils_v2';
import { BlobUploadType, RequestParameters } from '~/backend/backend-client';
import { BackendV2Client } from '~/backend/backend-v2-client';
import { CompositeCbctClient } from '~/backend/composite-cbct-client';
import { RegistrationClient } from '~/backend/registration-client';

/**
 * Per-task-family configuration for the shared v2 job processor.
 * Each v2 workflow type provides:
 *  - how to split `payload.filesToUpload` into the two input datasets
 *    (by a unique filename prefix the task hook stamps on each blob),
 *  - labels used in error messages,
 *  - a type guard for the expected client subclass, and
 *  - a task-creation closure that builds the family-specific request
 *    body and calls the matching client method.
 */
type V2JobSpec<C extends BackendV2Client> = {
    primaryPrefix: string;
    secondaryPrefix: string;
    primaryLabel: string;
    secondaryLabel: string;
    jobLabel: string;
    isClient: (client: unknown) => client is C;
    submitTask: (
        client: C,
        request: RequestParameters,
        datasetIds: { primary: string; secondary: string },
        payload: NonNullable<Job['payload']>
    ) => Promise<string | null>;
};

function splitBlobsByPrefix(
    files: BlobUploadType[],
    primaryPrefix: string,
    secondaryPrefix: string
): { primary: BlobUploadType[]; secondary: BlobUploadType[] } {
    const primary: BlobUploadType[] = [];
    const secondary: BlobUploadType[] = [];
    for (const file of files) {
        if (!file.filename) {
            continue;
        }
        if (file.filename.startsWith(primaryPrefix)) {
            primary.push(file);
        } else if (file.filename.startsWith(secondaryPrefix)) {
            secondary.push(file);
        }
    }
    return { primary, secondary };
}

const REGISTRATION_JOB_SPEC: V2JobSpec<RegistrationClient> = {
    primaryPrefix: 'source_',
    secondaryPrefix: 'target_',
    primaryLabel: 'source',
    secondaryLabel: 'target',
    jobLabel: 'registration',
    isClient: (client): client is RegistrationClient =>
        client instanceof RegistrationClient,
    submitTask: (client, request, datasetIds, payload) =>
        createRegistrationTask(client, request, {
            task_id: request.taskUID ?? '',
            model_name: 'reg_inter',
            // The adapt+ backend expects the dataset role to be
            // swapped relative to our primary/secondary framing —
            // `source_image` carries the *target* (fixed) dataset id
            // and vice-versa. Kept as-is from the original
            // `processRegistrationJob` implementation.
            source_image: datasetIds.secondary,
            target_image: datasetIds.primary,
            matrix: payload.transformationMatrix || [],
            matrix_shape: [1, 1, 1, 1],
            matrix_convention: 'row_first',
            ...(payload.voiBounds && { voi_bounds: payload.voiBounds }),
        } as Parameters<typeof createRegistrationTask>[2]),
};

const COMPOSITE_CBCT_JOB_SPEC: V2JobSpec<CompositeCbctClient> = {
    primaryPrefix: 'cbct_',
    secondaryPrefix: 'ct_',
    primaryLabel: 'CBCT',
    secondaryLabel: 'base CT',
    jobLabel: 'composite CBCT',
    isClient: (client): client is CompositeCbctClient =>
        client instanceof CompositeCbctClient,
    submitTask: (client, request, datasetIds) =>
        createCompositeCbctTask(client, request, {
            task_id: request.taskUID ?? '',
            cbct_image: datasetIds.primary,
            ct_image: datasetIds.secondary,
        }),
};

// Each spec narrows to its own client subclass; the registry holds
// them in their widened `BackendV2Client` form so `processV2Job` can
// look them up uniformly by workflow type. `isClient` inside each
// spec guards the unsafe-looking cast — `submitTask` is never
// invoked with a client that failed the guard.
const V2_JOB_SPECS: Partial<{
    [K in WorkflowTypes]: V2JobSpec<BackendV2Client>;
}> = {
    [WorkflowTypes.REGISTER_IMAGES]:
        REGISTRATION_JOB_SPEC as unknown as V2JobSpec<BackendV2Client>,
    [WorkflowTypes.COMPOSITE_CBCT]:
        COMPOSITE_CBCT_JOB_SPEC as unknown as V2JobSpec<BackendV2Client>,
};

// Module-scoped to survive React StrictMode's dev-only mount/unmount/mount
// cycle. A `useRef` Set is reinitialised on the remount, but the in-flight
// `processV2Job` from the first mount is still running — the fresh ref
// would let the effect re-fire `processV2Job` for the same job, which then
// invokes `onLoad` twice and double-counts result-file processing.
const currentlyProcessingJobIds = new Set<string>();

/**
 * A hook for orchestrating backend jobs.
 * It processes jobs in the queue, handling file uploads, polling for results, and downloading final results.
 * @returns {void}
 */
export function useBackendJobOrchestrator() {
    const { jobQueue, setJobStatus, updateJob } = useJobManagement();
    const expandJobQueue = useSetRecoilState(jobQueueExpandedState);

    const finalizeDownload = useCallback(
        (jobId: string, results: DownloadedResults, pollData: PollData) => {
            const hasBackendErrors = results.errors.length > 0;
            const hasDownloadErrors = results.downloadErrors.length > 0;

            if (hasBackendErrors || hasDownloadErrors) {
                const allErrors = [
                    ...results.errors,
                    ...results.downloadErrors,
                ];
                updateJob(jobId, {
                    status: 'Error',
                    error: `Download failed: ${allErrors.join('; ')}`,
                    downloadRetryData: hasDownloadErrors
                        ? { type: 'backend', pollData }
                        : undefined,
                });
            } else {
                updateJob(jobId, {
                    status: 'Done',
                    results: results.files,
                    downloadRetryData: undefined,
                });
            }
        },
        [updateJob]
    );

    const processJob = useCallback(
        async (jobToProcess: Job) => {
            const { id: jobId, client, payload } = jobToProcess;
            if (!payload) {
                setJobStatus(jobId, 'Error', `Job payload is missing.`);
                return;
            }
            if (!client) {
                setJobStatus(
                    jobId,
                    'Error',
                    `Configuration error: No client for ${jobToProcess.type}.`
                );
                return;
            }

            try {
                setJobStatus(jobId, 'Uploading');
                await uploadFiles(
                    jobId,
                    client,
                    payload,
                    (successfullySubmittedCount) => {
                        updateJob(jobId, {
                            uploadedItems: successfullySubmittedCount,
                        });
                    }
                );

                if (jobToProcess.type === WorkflowTypes.DATA_UPLOAD) {
                    setJobStatus(jobId, 'Done');
                    return;
                }

                setJobStatus(jobId, 'Processing');
                const pollData = await pollForResults(jobId, client);

                setJobStatus(jobId, 'Downloading');
                const results = await downloadBackendResults(
                    jobId,
                    client,
                    pollData
                );
                finalizeDownload(jobId, results, pollData);
            } catch (error) {
                if (typeof error === 'string') {
                    setJobStatus(jobId, 'Error', error);
                } else if (error instanceof Error) {
                    setJobStatus(jobId, 'Error', error.message);
                } else {
                    setJobStatus(jobId, 'Error', 'Error processing task.');
                }
            }
        },
        [setJobStatus, updateJob, finalizeDownload]
    );

    const retryDownload = useCallback(
        async (jobToRetry: Job) => {
            const { id: jobId, client, downloadRetryData } = jobToRetry;
            if (
                !client ||
                !downloadRetryData ||
                downloadRetryData.type !== 'backend'
            ) {
                setJobStatus(jobId, 'Error', 'Cannot retry: missing data.');
                return;
            }

            try {
                setJobStatus(jobId, 'Downloading');
                const results = await downloadBackendResults(
                    jobId,
                    client,
                    downloadRetryData.pollData
                );
                finalizeDownload(jobId, results, downloadRetryData.pollData);
            } catch (error) {
                if (error instanceof Error) {
                    setJobStatus(jobId, 'Error', error.message);
                } else {
                    setJobStatus(jobId, 'Error', 'Retry download failed.');
                }
            }
        },
        [setJobStatus, finalizeDownload]
    );

    const processV2Job = useCallback(
        async <C extends BackendV2Client>(
            jobToProcess: Job,
            spec: V2JobSpec<C>
        ) => {
            const { id: jobId, client, payload } = jobToProcess;

            if (!spec.isClient(client)) {
                setJobStatus(
                    jobId,
                    'Error',
                    `Invalid client for ${spec.jobLabel} job.`
                );
                return;
            }

            if (!payload) {
                setJobStatus(jobId, 'Error', 'Missing job payload.');
                return;
            }

            try {
                setJobStatus(jobId, 'Uploading');

                const taskUID = payload.backendParameters?.taskUID ?? jobId;

                const { primary: primaryBlobs, secondary: secondaryBlobs } =
                    splitBlobsByPrefix(
                        payload.filesToUpload,
                        spec.primaryPrefix,
                        spec.secondaryPrefix
                    );

                const primaryDatasetId = await createImageDataset(
                    client,
                    payload.backendParameters,
                    taskUID,
                    primaryBlobs.length
                );
                if (!primaryDatasetId) {
                    setJobStatus(
                        jobId,
                        'Error',
                        `Failed creating ${spec.primaryLabel} dataset.`
                    );
                    return;
                }

                const secondaryDatasetId = await createImageDataset(
                    client,
                    payload.backendParameters,
                    taskUID,
                    secondaryBlobs.length
                );
                if (!secondaryDatasetId) {
                    setJobStatus(
                        jobId,
                        'Error',
                        `Failed creating ${spec.secondaryLabel} dataset.`
                    );
                    return;
                }

                const primaryUploaded = await uploadDatasetFiles(
                    client,
                    payload.backendParameters,
                    primaryDatasetId,
                    primaryBlobs
                );
                if (primaryUploaded !== primaryBlobs.length) {
                    setJobStatus(
                        jobId,
                        'Error',
                        `Failed uploading ${spec.primaryLabel} dataset.`
                    );
                    return;
                }

                const secondaryUploaded = await uploadDatasetFiles(
                    client,
                    payload.backendParameters,
                    secondaryDatasetId,
                    secondaryBlobs
                );
                if (secondaryUploaded !== secondaryBlobs.length) {
                    setJobStatus(
                        jobId,
                        'Error',
                        `Failed uploading ${spec.secondaryLabel} dataset.`
                    );
                    return;
                }

                setJobStatus(jobId, 'Processing');

                const taskRequestParameters: RequestParameters = {
                    action: `create${spec.jobLabel}Task`,
                    taskUID,
                    sessionId: payload.backendParameters?.sessionId,
                };

                const taskId = await spec.submitTask(
                    client,
                    taskRequestParameters,
                    {
                        primary: primaryDatasetId,
                        secondary: secondaryDatasetId,
                    },
                    payload
                );
                if (!taskId) {
                    setJobStatus(
                        jobId,
                        'Error',
                        `Failed to create ${spec.jobLabel} task.`
                    );
                    return;
                }

                const resultData = await monitorCreatedTask(
                    client,
                    taskRequestParameters,
                    taskId
                );
                if (!resultData || !resultData.resultIds?.length) {
                    setJobStatus(
                        jobId,
                        'Error',
                        'No results returned from backend.'
                    );
                    return;
                }

                setJobStatus(jobId, 'Downloading');

                const resultFiles = await downloadTaskResults(
                    client,
                    taskRequestParameters,
                    taskId,
                    resultData.resultIds
                );
                if (resultFiles.length === 0) {
                    setJobStatus(
                        jobId,
                        'Error',
                        'No result files were downloaded.'
                    );
                    return;
                }

                updateJob(jobId, {
                    results: resultFiles,
                    status: 'Done',
                });

                // Auto-loading the results is driven by the JobItem
                // useEffect that watches for `status === 'Done'` and
                // calls `job.onLoad?.(job)` itself, gated by the user's
                // `autoLoadResultsState` preference. Calling onLoad here
                // as well caused a race where onLoad ran twice (once
                // via this direct call, once via JobItem's effect),
                // duplicating result-file processing.
                await deleteTaskResult(client, taskRequestParameters, taskId);
            } catch (error) {
                handleError(error, `Error processing ${spec.jobLabel} job.`);
                setJobStatus(
                    jobId,
                    'Error',
                    (error as Error)?.message ?? 'Unknown error.'
                );
            }
        },
        [setJobStatus, updateJob]
    );

    useEffect(() => {
        const jobsToProcess = jobQueue.jobs.filter(
            (job) =>
                (job.status === 'Queued' || job.status === 'RetryDownload') &&
                !currentlyProcessingJobIds.has(job.id)
        );
        if (jobsToProcess.length > 0) {
            expandJobQueue(true);
        }
        jobsToProcess.forEach((job) => {
            currentlyProcessingJobIds.add(job.id);
            const v2Spec = V2_JOB_SPECS[job.type];
            if (job.status === 'RetryDownload') {
                retryDownload(job).finally(() => {
                    currentlyProcessingJobIds.delete(job.id);
                });
            } else if (v2Spec) {
                processV2Job(job, v2Spec).finally(() => {
                    currentlyProcessingJobIds.delete(job.id);
                });
            } else {
                processJob(job).finally(() => {
                    currentlyProcessingJobIds.delete(job.id);
                });
            }
        });
    }, [
        jobQueue.jobs,
        processV2Job,
        processJob,
        retryDownload,
        setJobStatus,
        expandJobQueue,
    ]);
}
